import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        fileIcon:
          "absolute -bottom-1 left-0 right-0 mx-auto w-max !rounded-[4px] !px-1 !py-[1px] !text-[8px] !font-bold uppercase !leading-none border-none text-white shadow-sm",
        track: "border-transparent bg-medium text-[#682F12]",
        choices: "border-transparent bg-choice text-[#124B68]",
        specific: "border-transparent bg-specific text-[#351A75]",
        rejected: "border-transparent bg-high text-white",
        qualify:
          "border-transparent bg-[#BA37FB] text-white hover:bg-[#a52be0]",

        green: "border-transparent bg-emerald-100 text-emerald-700",
        /** Integration not configured — light red from --color-high @ 20% opacity, single line */
        disconnected:
          "border border-[color-mix(in_srgb,var(--color-high)_28%,transparent)] bg-[color-mix(in_srgb,var(--color-high)_20%,transparent)] text-[var(--color-high)] whitespace-nowrap",
        gray: "border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200",
        indigo:
          "border-transparent bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
        info: "border-transparent bg-[#dde9ff] text-[#335cff]",
        warning: "border-transparent bg-transparent text-[#ff8447]",
        count:
          "border-transparent bg-[radial-gradient(239.95%_185.59%_at_63.38%_-7.95%,#319b72_0%,#102f23_33.08%,#319b72_97.6%)] !text-white text-b4-mid",
        newLead:
          "border-[#D1E6FF] border bg-[linear-gradient(180deg,#C0DCFF_7.69%,#CAE2FF_68.27%)] text-[#122368] shadow-[inset_0_5px_4px_0_rgba(217,234,255,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[12px]",
        followup:
          "border-[#FFE3D1] border bg-[linear-gradient(180deg,#E5C3AC_7.69%,#FFDFCA_68.27%)] shadow-[inset_0_5px_4px_0_#F5DFD0,0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#682F12] text-[12px]",
        verified:
          "border border-[#C9F5DE] bg-[linear-gradient(180deg,#9ACDB2_7.69%,#C2F5DA_68.27%)] shadow-[inset_0_5px_4px_0_rgba(208,245,226,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#123321] text-[12px]",
        waiting:
          "border border-[#FFFED1] bg-[linear-gradient(180deg,#F5F4B8_7.69%,#F5F4C2_68.27%)] shadow-[inset_0_5px_4px_0_rgba(255,254,217,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#5F5E10] text-[12px]",
        interested:
          "border border-[#E5D1FF] bg-[linear-gradient(180deg,#DBC0FF_7.69%,#E1CAFF_68.27%)] shadow-[inset_0_5px_4px_0_rgba(233,217,255,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#351A75] text-[12px]",
        closed:
          "border border-[#ECECEC] bg-[linear-gradient(180deg,#DBDBDB_7.69%,#F3F3F3_68.27%)] shadow-[inset_0_5px_4px_0_rgba(244,244,244,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#4A4A4A] text-[12px]",
        trackPending:
          "border border-[#FFE0B2] bg-[linear-gradient(180deg,#FFB74D_7.69%,#FFD54F_68.27%)] shadow-[inset_0_5px_4px_0_rgba(255,224,178,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-[#4E2800] text-[12px]",
        trackActive:
          "border border-[#C9F5DE] bg-[linear-gradient(180deg,#66BB6A_7.69%,#81C784_68.27%)] shadow-[inset_0_5px_4px_0_rgba(200,245,218,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-white text-[12px]",
        trackRejected:
          "border border-[#FFCDD2] bg-[linear-gradient(180deg,#EF5350_7.69%,#E57373_68.27%)] shadow-[inset_0_5px_4px_0_rgba(255,205,210,0.96),0_2px_2px_0_rgba(0,0,0,0.08)] rounded-[var(--radius-full,999px)] text-white text-[12px]",
      },
    },
    defaultVariants: {
      variant: "track",
    },
  }
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <div
      style={{ fontWeight: "400" }}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {/* Conditionally render the Star icon if variant is 'qualify'. */}
      {/* `text-white` is set explicitly so when this badge is nested */}
      {/* inside a SelectItem (which has a `[&_svg:not([class*='text-'])]` */}
      {/* rule that paints SVGs muted-gray) the star stays white. */}
      {variant === "qualify" && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="mr-1 h-3 w-3 text-white"
        >
          <path
            fillRule="evenodd"
            d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
